import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const LYRA_ATTENDING_QUEUE_NAMES = [
    "usuario lyra",
    "usuario operacional da lyra",
    "atendimento lyra - leads",
];
const LYRA_HANDOFF_QUEUE_NAME = "aguardando consultor - lyra";

const normalizarTexto = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// ✅ Função de ordenação que prioriza tickets com mensagens não lidas
const ticketSortAsc = (a, b) => {
    // Tickets com mensagens não lidas sempre ficam no topo
    if (a.unreadMessages > 0 && b.unreadMessages === 0) return -1;
    if (a.unreadMessages === 0 && b.unreadMessages > 0) return 1;
    
    // Se ambos têm ou não têm mensagens não lidas, ordenar por updatedAt
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
    // Tickets com mensagens não lidas sempre ficam no topo
    if (a.unreadMessages > 0 && b.unreadMessages === 0) return -1;
    if (a.unreadMessages === 0 && b.unreadMessages > 0) return 1;
    
    // Se ambos têm ou não têm mensagens não lidas, ordenar por updatedAt
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

// Merge incoming ticket with existing, preserving tags quando o payload não traz (evita tag sumir ao receber mensagem / no servidor)
const mergeTicketPreservingTags = (existing, incoming) => {
    const merged = { ...existing, ...incoming };
    if (incoming.contact && existing.contact) {
        merged.contact = { ...existing.contact, ...incoming.contact };
        const hasIncomingContactTags = Array.isArray(incoming.contact?.tags) && incoming.contact.tags.length > 0;
        if (!hasIncomingContactTags && Array.isArray(existing.contact?.tags) && existing.contact.tags.length > 0) {
            merged.contact.tags = existing.contact.tags;
        }
    } else if (existing.contact && !incoming.contact) {
        merged.contact = existing.contact;
    }
    const hasIncomingTicketTags = Array.isArray(incoming.tags) && incoming.tags.length > 0;
    if (!hasIncomingTicketTags && Array.isArray(existing.tags) && existing.tags.length > 0) {
        merged.tags = existing.tags;
    }
    return merged;
};

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;

    if (action.type === "REPLACE_TICKETS") {
        const newTickets = Array.isArray(action.payload) ? [...action.payload] : [];

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? newTickets.sort(ticketSortAsc) : newTickets.sort(ticketSortDesc);
        }

        return newTickets;
    }
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = Array.isArray(action.payload) ? action.payload : [];

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = mergeTicketPreservingTags(state[ticketIndex], ticket);
            } else {
                state.push(ticket);
            }
        });
        // ✅ Sempre aplicar ordenação para manter tickets com unreadMessages no topo
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = mergeTicketPreservingTags(state[ticketIndex], ticket);
        } else {
            // ✅ Se tem mensagens não lidas, adicionar no topo, senão adicionar no final
            if (ticket.unreadMessages > 0) {
                state.unshift(ticket);
            } else {
                state.push(ticket);
            }
        }
        // ✅ Sempre aplicar ordenação para manter tickets com unreadMessages no topo
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            const merged = mergeTicketPreservingTags(state[ticketIndex], ticket);
            state[ticketIndex] = merged;
            // ✅ Se tem mensagens não lidas, mover para o topo
            if (ticket.unreadMessages > 0) {
                state.unshift(state.splice(ticketIndex, 1)[0]);
            }
        } else {
            if (action.status === action.payload.status) {
                // ✅ Se tem mensagens não lidas, adicionar no topo
                if (ticket.unreadMessages > 0) {
                    state.unshift(ticket);
                } else {
                    state.push(ticket);
                }
            }
        }
        // ✅ Sempre aplicar ordenação para manter tickets com unreadMessages no topo
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            const existing = state[ticketIndex];
            const existingContact = existing.contact;
            const mergedContact = existingContact
                ? { ...existingContact, ...contact }
                : contact;
            const hasIncomingTags = Array.isArray(contact?.tags) && contact.tags.length > 0;
            if (!hasIncomingTags && Array.isArray(existingContact?.tags) && existingContact.tags.length > 0) {
                mergedContact.tags = existingContact.tags;
            }
            state[ticketIndex] = { ...existing, contact: mergedContact };
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        updateUnreadCount,
        updateTotalCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        refreshTrigger,
        supervisedUserId,
        supervisedDate,
        scopeUser,
        onTicketStatusChange
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const effectiveUser = scopeUser || user;
    const { profile, queues } = effectiveUser;
    const isSupervisorScope = Boolean(supervisedUserId);
    const isLyraSupervisorUser =
        isSupervisorScope &&
        (
            normalizarTexto(effectiveUser?.name) === "atendewhatsapp" ||
            normalizarTexto(effectiveUser?.name) === "usuario lyra" ||
            normalizarTexto(effectiveUser?.email).includes("atendewhatsapp")
        );
    const showTicketWithoutQueue = effectiveUser?.allTicket === 'enable' || effectiveUser?.allTicket === 'enabled';
    const supervisorQueueIds = isSupervisorScope && Array.isArray(effectiveUser?.queues)
        ? effectiveUser.queues.map((queue) => queue.id).filter(Boolean)
        : [];
    const supervisorQueueIdSet = new Set(supervisorQueueIds.map((queueId) => Number(queueId)).filter(Boolean));
    const effectiveQueueIds = Array.isArray(effectiveUser?.queues)
        ? effectiveUser.queues.map((queue) => Number(queue.id)).filter(Boolean)
        : [];
    const effectiveQueueIdSet = new Set(effectiveQueueIds);
    const selectedQueueIdSet = useMemo(
        () => new Set((selectedQueueIds || []).map((queueId) => Number(queueId)).filter(Boolean)),
        [selectedQueueIds]
    );
    const lyraAttendingQueueIdSet = useMemo(() => new Set(
        (Array.isArray(effectiveUser?.queues) ? effectiveUser.queues : [])
            .filter((queue) => LYRA_ATTENDING_QUEUE_NAMES.includes(normalizarTexto(queue?.name)))
            .map((queue) => Number(queue.id))
            .filter(Boolean)
    ), [effectiveUser]);
    const lyraHandoffQueueIdSet = useMemo(() => new Set(
        (Array.isArray(effectiveUser?.queues) ? effectiveUser.queues : [])
            .filter((queue) => normalizarTexto(queue?.name) === LYRA_HANDOFF_QUEUE_NAME)
            .map((queue) => Number(queue.id))
            .filter(Boolean)
    ), [effectiveUser]);
    const pertenceVisaoOperacionalLyraAtual = (ticket, ticketStatus = status) => {
        if (!ticket || ticket.userId != null) return false;

        const queueId = Number(ticket.queueId);
        const queueName = normalizarTexto(ticket?.queue?.name);
        const isAttendingQueue =
            lyraAttendingQueueIdSet.has(queueId) ||
            LYRA_ATTENDING_QUEUE_NAMES.includes(queueName);
        const isHandoffQueue =
            lyraHandoffQueueIdSet.has(queueId) ||
            queueName === LYRA_HANDOFF_QUEUE_NAME;

        const isAttending =
            isAttendingQueue &&
            ["open", "pending"].includes(ticket.status);
        const isWaitingHuman =
            isHandoffQueue &&
            ticket.status === "pending";

        if (ticketStatus === "open") return isAttending;
        if (ticketStatus === "pending") return isWaitingHuman;
        if (ticketStatus === "flow") return false;
        if (ticketStatus && ticketStatus !== "search") return false;

        return isAttending || isWaitingHuman;
    };
    const matchesAllowedQueue = (ticket) => {
        if (!ticket?.queueId) return false;
        return effectiveQueueIdSet.has(Number(ticket.queueId));
    };
    const matchesSelectedQueue = (ticket) => {
        if (!selectedQueueIdSet.size) return true;
        if (!ticket?.queueId) return showTicketWithoutQueue;
        return selectedQueueIdSet.has(Number(ticket.queueId));
    };
    const matchesAllowedAndSelectedQueue = (ticket) =>
        matchesAllowedQueue(ticket) && matchesSelectedQueue(ticket);
    const companyId = user.companyId;
    const selectedQueueIdsKey = useMemo(() => JSON.stringify(selectedQueueIds || []), [selectedQueueIds]);
    const tagsKey = useMemo(() => JSON.stringify(tags || []), [tags]);
    const usersKey = useMemo(() => JSON.stringify(users || []), [users]);
    const whatsappIdsKey = useMemo(() => JSON.stringify(whatsappIds || []), [whatsappIds]);
    const statusFilterKey = useMemo(() => JSON.stringify(statusFilter || []), [statusFilter]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tagsKey, usersKey, forceSearch, selectedQueueIdsKey, whatsappIdsKey, statusFilterKey, sortTickets, searchOnMessages, supervisedUserId, supervisedDate]);

    const { tickets, hasMore, loading, count: totalCount } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: selectedQueueIdsKey,
        whatsappIds: whatsappIdsKey,
        statusFilter: statusFilterKey,
        userFilter,
        sortTickets,
        forceSearch,
        refreshTrigger,
        supervisedUserId,
        supervisedDate,
        authUserId: user?.id
    });

    const ticketBelongsToCurrentScope = (ticket) => {
        if (!ticket) return false;

        if (isLyraSupervisorUser) {
            return matchesSelectedQueue(ticket) && pertenceVisaoOperacionalLyraAtual(ticket, status);
        }

        const targetUserId = Number(supervisedUserId || user?.id);
        const ticketUserId = ticket?.userId == null ? null : Number(ticket.userId);
        const belongsToTargetUser = ticketUserId != null && ticketUserId === targetUserId;
        const isPendingFromAllowedQueue =
            ticket.status === "pending" &&
            ticket.userId == null &&
            matchesAllowedAndSelectedQueue(ticket) &&
            (!isSupervisorScope || supervisorQueueIdSet.has(Number(ticket.queueId)));

        if (isSupervisorScope) {
            // Em supervisão, o backend já aplica a regra de empresa, usuário e filas.
            // Aqui evitamos uma segunda filtragem por filas do objeto local, que pode
            // vir incompleto e esconder tickets válidos na VPS.
            if (status === "pending") {
                return isPendingFromAllowedQueue;
            }
            if (status === "search") return belongsToTargetUser || isPendingFromAllowedQueue;
            return belongsToTargetUser;
        }

        if (profile === "user") {
            if (status === "pending") return isPendingFromAllowedQueue;
            if (status === "search") return belongsToTargetUser || isPendingFromAllowedQueue;
            return belongsToTargetUser;
        }

        return matchesSelectedQueue(ticket);
    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            const scopedTickets = tickets.filter(ticketBelongsToCurrentScope);

            dispatch({
                type: pageNumber === 1 ? "REPLACE_TICKETS" : "LOAD_TICKETS",
                payload: scopedTickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets, companyId, pageNumber, status, sortTickets, supervisedUserId, supervisedDate, selectedQueueIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
    const shouldUpdateTicket = ticket => {
      return ticketBelongsToCurrentScope(ticket);
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

      const notBelongsToUserQueues = (ticket) =>
        isLyraSupervisorUser
              ? !(matchesSelectedQueue(ticket) && pertenceVisaoOperacionalLyraAtual(ticket, status))
              : (!isSupervisorScope && profile === "user")
              ? !shouldUpdateTicket(ticket)
              : isSupervisorScope
              ? !shouldUpdateTicket(ticket)
              : !matchesSelectedQueue(ticket);

        const belongsToFlowList = (ticket) =>
            ticket && ticket.flowWebhook === true && ticket.flowTriggeredManually === true && (ticket.status === "open" || ticket.status === "pending");

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // Aba Flow: ticket tem status "open" no backend; considerar por flowWebhook
            const updateMatchesStatus = status === "flow"
                ? belongsToFlowList(data.ticket)
                : isLyraSupervisorUser
                    ? pertenceVisaoOperacionalLyraAtual(data.ticket, status)
                    : (data.ticket && data.ticket.status === status);
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && updateMatchesStatus) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
                // Ticket entrou ou foi atualizado nesta aba (ex.: devolvido para Aguardando) -> atualizar contador
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // Aba Flow: quando fluxo termina (flowWebhook false), remover da lista
            if (status === "flow" && data.action === "update" && data.ticket &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === "open" && !data.ticket.flowWebhook) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // create: novo ticket entrou na aba -> atualizar contadores
            if (data.action === "create" && data.ticket && shouldUpdateTicket(data.ticket) &&
                (status === "flow"
                    ? belongsToFlowList(data.ticket)
                    : isLyraSupervisorUser
                        ? pertenceVisaoOperacionalLyraAtual(data.ticket, status)
                        : data.ticket.status === status)) {
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // update: ticket saiu desta aba (ex.: pending -> open) -> remover da lista e atualizar contadores
            if (data.action === "update" && data.ticket && shouldUpdateTicket(data.ticket) && !updateMatchesStatus &&
                (status === "flow" ? !belongsToFlowList(data.ticket) : data.ticket.status !== status)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            const messageMatchesStatus = status === "flow"
                ? belongsToFlowList(data.ticket)
                : isLyraSupervisorUser
                    ? pertenceVisaoOperacionalLyraAtual(data.ticket, status)
                    : (data.ticket && data.ticket.status === status);
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && messageMatchesStatus) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // ✅ CORREÇÃO: Atualizar ticket quando mensagem é atualizada (ex: deletada)
            if (data.action === "update" && data.ticket &&
                shouldUpdateTicket(data.ticket) && messageMatchesStatus) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIdsKey, tagsKey, usersKey, profile, queues, sortTickets, showTicketWithoutQueue, supervisedUserId, onTicketStatusChange, effectiveUser, isLyraSupervisorUser]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        if (typeof updateUnreadCount === "function") {
            const unreadTickets = ticketsList.filter(t => Number(t.unreadMessages) > 0).length;
            updateUnreadCount(unreadTickets);
        }
        // Sincronizar contador total: quando a lista diminui (ex.: ticket fechado/ignorado), usar ticketsList.length para o contador atualizar na hora; quando a lista cresce (socket), usar o maior
        if (typeof updateTotalCount === "function") {
            const total = totalCount !== undefined && totalCount !== null
                ? (ticketsList.length < totalCount ? ticketsList.length : Math.max(ticketsList.length, totalCount))
                : ticketsList.length;
            updateTotalCount(total);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList, totalCount]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

   const deveAplicarFiltroFilaNaRenderizacao = () => {
       if (isLyraSupervisorUser) return true;
       if (isSupervisorScope) return status === "pending";
       if (profile === "user") return status === "pending";
       return true;
   };

    const aplicaFiltroFilaNaRenderizacao = deveAplicarFiltroFilaNaRenderizacao();

    if (status && status !== "search") {
        if (status === "flow") {
            // Aba Flow: apenas fluxo disparado manualmente (n?o integra??o autom?tica)
            ticketsList = ticketsList.filter(ticket => {
                if (!ticketBelongsToCurrentScope(ticket)) return false;
                if (aplicaFiltroFilaNaRenderizacao && !matchesSelectedQueue(ticket)) return false;

                if (isLyraSupervisorUser) {
                    return pertenceVisaoOperacionalLyraAtual(ticket, "flow");
                }

                return ticket.flowWebhook === true &&
                    ticket.flowTriggeredManually === true &&
                    (ticket.status === "open" || ticket.status === "pending");
            });
        } else if (status === "open") {
            // Na aba Open n?o mostrar tickets em fluxo manual (esses s? na aba Flow)
            ticketsList = ticketsList.filter(ticket => {
                if (!ticketBelongsToCurrentScope(ticket)) return false;
                if (aplicaFiltroFilaNaRenderizacao && !matchesSelectedQueue(ticket)) return false;

                if (isLyraSupervisorUser) {
                    return pertenceVisaoOperacionalLyraAtual(ticket, "open");
                }

                return ticket.status === "open" &&
                    (isSupervisorScope || !!ticket.userId) &&
                    !(ticket.flowWebhook === true && ticket.flowTriggeredManually === true);
            });
        } else {
            ticketsList = ticketsList.filter(ticket => {
                if (!ticketBelongsToCurrentScope(ticket)) return false;
                if (aplicaFiltroFilaNaRenderizacao && !matchesSelectedQueue(ticket)) return false;
                if (!isLyraSupervisorUser) return ticket.status === status;
                return pertenceVisaoOperacionalLyraAtual(ticket, status);
            });
        }
    } else {
        ticketsList = ticketsList.filter(ticketBelongsToCurrentScope);
    }


    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                    isLyraSupervisorView={isSupervisorScope && isLyraSupervisorUser}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
